// BirthChartTab.tsx
import React, { useState } from "react";
import { useLaneCopy } from "../../../../utils/laneCopy";
import { Moon } from "lucide-react";
import AstriaVietnamShell, {
  SHELL_COLORS_ASTRIA_VIETNAM,
} from "../AstriaVietnamShell";
import {
  AstriaVietnamResultCard,
  AstriaVietnamFieldBlock,
  AstriaVietnamLoadingState,
  AstriaVietnamErrorState,
  AstriaVietnamChatCta,
} from "../AstriaVietnamCard";
import {
  WizardStep,
  WizardNav,
  WizardChips,
  WizardDateFields,
  WizardDateFieldsValue,
  formatDateFieldsValue,
} from "../AstriaVietnamWizard";
import { useAstriaVietnamTab } from "../useAstriaVietnamTab";
import { AstriaVietnamBirthChartData } from "../../../../contexts/ChatContext";
import { useAstriaVietnamLanguage } from "../astriaVietnamLanguage";
import { ASTRIA_VIETNAM_COPY } from "../astriaVietnamCopy";

// SubCategory name must contain "Birth Chart" — matches
// resolveVietnamSubcategoryEntry in
// backend/helper/vietnam/astriaVietnamPromptService.js.
const SUBCATEGORY_NAME = "Birth Chart";

const BirthChartTab: React.FC = () => {
  const lang = useAstriaVietnamLanguage();
  const copy = useLaneCopy("vn", ASTRIA_VIETNAM_COPY);
  const t = copy.birthChart;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } =
    useAstriaVietnamTab({ subCategoryName: SUBCATEGORY_NAME, language: lang });

  const [dateValue, setDateValue] = useState<WizardDateFieldsValue>({
    day: "",
    month: "",
    year: "",
    hourChi: null,
  });
  const [gender, setGender] = useState<string | null>(null);

  const started = !!message || loading || !!error;

  const normalizedDob = formatDateFieldsValue(dateValue);
  const step1Valid = !!normalizedDob && !!dateValue.hourChi && !!gender;

  const handleSubmit = () => {
    send(
      "Read my Vietnamese birth chart (Tử Vi). Please reflect gently on my life direction and current cycle.",
      {
        dob: normalizedDob || undefined,
        dob_hour: dateValue.hourChi || undefined,
        gender: gender || undefined,
      },
    );
  };

  const birthChartData = message?.astriaVietnamData as
    | AstriaVietnamBirthChartData
    | undefined;

  if (started) {
    return (
      <AstriaVietnamShell active="birth-chart" lang={lang}>
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
              marginBottom: 4,
            }}
          >
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <AstriaVietnamLoadingState label={t.loading} />}

        {!loading && error && (
          <AstriaVietnamErrorState
            message={error}
            onRetry={handleSubmit}
            retryLabel={shared.tryAgain}
          />
        )}

        {!loading && !error && birthChartData && (
          <>
            <AstriaVietnamResultCard
              icon={<Moon className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              shareText={`${t.resultTitle}\n\n${birthChartData.summary}\n\n${birthChartData.guidance}`}
            >
              <AstriaVietnamFieldBlock label={t.fields.summary} value={birthChartData.summary} />
              <AstriaVietnamFieldBlock label={t.fields.cungMenhMeaning} value={birthChartData.cung_menh_meaning} />
              <AstriaVietnamFieldBlock label={t.fields.cungThanMeaning} value={birthChartData.cung_than_meaning} />
              <AstriaVietnamFieldBlock label={t.fields.hoaKhiEffect} value={birthChartData.hoa_khi_effect} />
              <AstriaVietnamFieldBlock label={t.fields.currentCycle} value={birthChartData.current_cycle} />
              <AstriaVietnamFieldBlock label={t.fields.guidance} value={birthChartData.guidance} />
            </AstriaVietnamResultCard>
            <button
              onClick={() => {
                reset();
                setDateValue({ day: "", month: "", year: "", hourChi: null });
                setGender(null);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}
            >
              {shared.startNewReading}
            </button>
            <AstriaVietnamChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !birthChartData && message && (
          <AstriaVietnamResultCard
            icon={<Moon className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
          >
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.75,
                color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
                whiteSpace: "pre-wrap",
              }}
            >
              {message.content}
            </p>
          </AstriaVietnamResultCard>
        )}
      </AstriaVietnamShell>
    );
  }

  return (
    <AstriaVietnamShell active="birth-chart" lang={lang}>
      <WizardStep stepKey="details" question={t.step1Question} helper={t.step1Helper}>
        <div style={{ marginBottom: 18 }}>
          <WizardDateFields
            value={dateValue}
            onChange={setDateValue}
            hourOptions={shared.dobHourOptions}
            hourLabel={shared.dobHourLabel}
            hourPlaceholder={shared.dobHourPlaceholder}
            dayPlaceholder={shared.dayPlaceholder}
            monthPlaceholder={shared.monthPlaceholder}
            yearPlaceholder={shared.yearPlaceholder}
          />
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
            marginBottom: 8,
          }}
        >
          {shared.genderLabel}
        </div>
        <WizardChips options={shared.genderOptions} value={gender} onChange={setGender} />

        <WizardNav
          showBack={false}
          nextDisabled={!step1Valid}
          onNext={handleSubmit}
          nextLabel={t.revealButton}
          backLabel={shared.back}
        />
      </WizardStep>
    </AstriaVietnamShell>
  );
};

export default BirthChartTab;
